import React, { useEffect, useRef } from "react";
import "./GuestIdUpload.scss";

const emptyPassenger = () => ({ name: "", front: null, back: null, frontUrl: "", backUrl: "" });

export const passengersComplete = (passengers) =>
  passengers.length > 0 && passengers.every((p) => p.name.trim() && p.front && p.back);

const SIDE_ICONS = { front: "fa-id-card", back: "fa-barcode" };

function IdTile({ id, side, file, url, onPick }) {
  return (
    <label htmlFor={id} className={`id-tile ${file ? "has-file" : ""}`}>
      <input
        id={id}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          if (picked) onPick(picked);
          e.target.value = "";
        }}
      />
      {url ? (
        <img src={url} alt={`ID ${side}`} />
      ) : (
        <span className="id-tile-empty">
          <i className={`fa-solid ${SIDE_ICONS[side]}`} aria-hidden="true"></i>
          <span>{side === "front" ? "Front of ID" : "Back of ID"}</span>
        </span>
      )}
      {file && <span className="id-tile-change">Change</span>}
    </label>
  );
}

function GuestIdUpload({ enabled, onToggle, passengers, setPassengers, capacity, leadTimeMessage }) {
  // Revoke preview URLs when the component goes away
  const passengersRef = useRef(passengers);
  passengersRef.current = passengers;
  useEffect(() => () => {
    passengersRef.current.forEach((p) => {
      if (p.frontUrl) URL.revokeObjectURL(p.frontUrl);
      if (p.backUrl) URL.revokeObjectURL(p.backUrl);
    });
  }, []);

  const handleToggle = (e) => {
    const checked = e.target.checked;
    onToggle(checked);
    if (checked && passengers.length === 0) setPassengers([emptyPassenger()]);
  };

  const setSide = (index, side, file) => {
    setPassengers((prev) => prev.map((p, i) => {
      if (i !== index) return p;
      if (p[`${side}Url`]) URL.revokeObjectURL(p[`${side}Url`]);
      return { ...p, [side]: file, [`${side}Url`]: URL.createObjectURL(file) };
    }));
  };

  const setName = (index, name) => {
    setPassengers((prev) => prev.map((p, i) => (i === index ? { ...p, name } : p)));
  };

  const removePassenger = (index) => {
    setPassengers((prev) => {
      const p = prev[index];
      if (p.frontUrl) URL.revokeObjectURL(p.frontUrl);
      if (p.backUrl) URL.revokeObjectURL(p.backUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const canAdd = !capacity || passengers.length < capacity;

  return (
    <div className="guest-id-section">
      <div className="guest-id-toggle">
        <input type="checkbox" id="outsideGuests" checked={enabled} onChange={handleToggle} />
        <label htmlFor="outsideGuests">I have passengers from outside El Gouna</label>
      </div>

      {enabled && (
        <>
          <div className="guest-id-disclaimer">
            <div className="terms-group" lang="en">
              <p>Passengers who are not El Gouna residents need their IDs approved before the trip:</p>
              <ul>
                <li>Approval takes up to 48 hours, so the trip must start at least 48 hours after you book.</li>
                <li>Your booking stays pending until the IDs are approved. We will email you with the decision.</li>
                <li>If entry is not approved, your deposit will be refunded. I agree that the refunded amount may not be my original full payment.</li>
                <li>ID images are used only for this approval and will be deleted within 3 days of the booking date.</li>
              </ul>
            </div>
            <div className="terms-group" lang="ar" dir="rtl">
              <p>الركاب من غير المقيمين في الجونة يجب الموافقة على هوياتهم قبل الرحلة:</p>
              <ul>
                <li>تستغرق الموافقة حتى 48 ساعة، لذلك يجب أن تبدأ الرحلة بعد 48 ساعة على الأقل من وقت الحجز.</li>
                <li>يظل الحجز قيد الانتظار حتى تتم الموافقة على الهويات، وسنرسل لك بريدًا إلكترونيًا بالقرار.</li>
                <li>في حالة عدم الموافقة على الدخول، سيتم استرداد العربون، وأوافق على أن المبلغ المسترد قد لا يساوي كامل المبلغ الذي دفعته.</li>
                <li>تُستخدم صور الهويات لهذه الموافقة فقط، وسيتم حذفها خلال 3 أيام من تاريخ الحجز.</li>
              </ul>
            </div>
          </div>

          {leadTimeMessage && <p className="guest-id-warning">{leadTimeMessage}</p>}

          <p className="field-hint">
            Add the full name and the front and back of the ID for each passenger from outside El Gouna.
          </p>

          <div className="guest-id-list">
            {passengers.map((p, i) => (
              <div className="guest-id-card" key={i}>
                <div className="guest-id-card-header">
                  <span>Passenger {i + 1}</span>
                  {passengers.length > 1 && (
                    <button type="button" className="guest-id-remove" onClick={() => removePassenger(i)}
                      aria-label={`Remove passenger ${i + 1}`}>
                      <i className="fa-solid fa-trash" aria-hidden="true"></i>
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  className="guest-id-name"
                  value={p.name}
                  onChange={(e) => setName(i, e.target.value)}
                  placeholder="Full name"
                  maxLength={80}
                  aria-label={`Passenger ${i + 1} full name`}
                  required
                />
                <div className="guest-id-tiles">
                  <IdTile id={`id-front-${i}`} side="front" file={p.front} url={p.frontUrl}
                    onPick={(f) => setSide(i, "front", f)} />
                  <IdTile id={`id-back-${i}`} side="back" file={p.back} url={p.backUrl}
                    onPick={(f) => setSide(i, "back", f)} />
                </div>
              </div>
            ))}
          </div>

          {canAdd && (
            <button type="button" className="guest-id-add" onClick={() => setPassengers((prev) => [...prev, emptyPassenger()])}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Add passenger
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default GuestIdUpload;
